import { keyframes } from '@emotion/react'
import {
  createMultiStyleConfigHelpers,
  MultiStyleType,
  StyleThemingProps,
} from '~/utils/chakraUtils'
import { mergeMultiStyleConfig } from '~/utils/styleHelpers'

import { asChakraVariable } from '@mangovoice/design-system'
import { textStyleKeys } from '@mangovoice/design-system'
import { textStyles } from '@mangovoice/design-system'

const FaxListItemParts = [
  'numberText',
  'directionIcon',
  'statusText',
  'thumbnailContainer',
  'thumbnailImage',
  'skeletonContainer',
  'skeleton',
  'thumbnailBox',
] as const

export enum FaxListItemVariant {
  Normal = 'Normal',
  Failed = 'Failed',
  Queued = 'Queued',
}

const helpers = createMultiStyleConfigHelpers(FaxListItemParts)

const pendingTextColorKeyframes = keyframes`
  from {
    color: ${asChakraVariable({ default: 'brand.solid' })}
  }
  to {
    color: ${asChakraVariable({ default: 'brand.emphasized' })}
  }
`

export const FaxListItemStyle = helpers.defineMultiStyleConfig({
  baseStyle: {
    numberText: {
      ...textStyles[textStyleKeys.Body1],
    },
    directionIcon: {
      width: '1rem',
      height: '1rem',
      color: 'fg.default',
    },
    statusText: {
      ...textStyles[textStyleKeys.Body3],
      color: 'fg.default',
    },
    thumbnailContainer: {
      flexDirection: 'column',
      alignItems: 'center',
      alignSelf: 'stretch',
      flexShrink: '0',
      position: 'relative',
      overflow: 'hidden',
    },
    thumbnailImage: {
      position: 'absolute',
      objectFit: 'contain',
      objectPosition: 'center',
    },
    skeletonContainer: {
      height: 'full',
      width: 'full',
    },
    skeleton: {
      startColor: 'blue.100',
      endColor: 'transparent',
    },
    thumbnailBox: {
      border: '1px solid',
      borderColor: 'border.muted',
      boxSizing: 'border-box',
      position: 'absolute',
    },
  },
  variants: {
    [FaxListItemVariant.Normal]: {},
    [FaxListItemVariant.Queued]: {
      statusText: {
        color: 'brand.emphasized',
        animation: `${pendingTextColorKeyframes} 1.5s ease-in-out infinite alternate`,
      },
      directionIcon: {
        color: 'brand.emphasized',
      },
    },
    [FaxListItemVariant.Failed]: {
      statusText: {
        color: 'red.fg',
      },
      directionIcon: {
        color: 'red.fg',
      },
    },
  },
  defaultProps: {
    variant: FaxListItemVariant.Normal,
  },
})

export const useFaxListItemStyle = (props?: StyleThemingProps) => {
  return mergeMultiStyleConfig(FaxListItemStyle, props) as MultiStyleType<
    typeof FaxListItemParts
  >
}
